import React from "react";
import { Button } from "../../ui/button";
import { Languages, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDisplayLanguageOptional } from "@/contexts/DisplayLanguageContext";
import { getLanguageName, getLanguageFlag } from "@/lib/dataSources/shared/languageMappings";
import { usePlatformTranslation } from "@/hooks/usePlatformTranslation";
import IconProvider from "@/components/ui/IconProvider";

interface DisplayLanguageSelectorProps {
  /** The item type — hidden for "blueprint" (templates are not translatable) */
  itemType: "blueprint" | "library" | "dialogs" | "document" | "variable";
}

/**
 * Self-contained language selector that reads from the DisplayLanguageContext.
 * Renders a compact dropdown to switch the display language for TMS strings.
 *
 * Visibility rules (handled internally):
 *  - Hidden when itemType is "blueprint" (templates are not translatable)
 *  - Hidden when the game has no languages configured
 *  - Hidden when the DisplayLanguageContext is not available
 */
const DisplayLanguageSelector: React.FC<DisplayLanguageSelectorProps> = ({ itemType }) => {
  const { t } = usePlatformTranslation();
  const displayLanguageCtx = useDisplayLanguageOptional();
  // Don't render for templates or when the context / languages aren't available
  if (
    itemType === "blueprint" || itemType === "document" || itemType === "variable" || 
    !displayLanguageCtx ||
    !displayLanguageCtx.hasMultipleLanguages
  ) {
    return null;
  }

  const { displayLanguage, setDisplayLanguage, availableLanguages } = displayLanguageCtx;

  const currentLabel = displayLanguage
    ? `${getLanguageFlag(displayLanguage)} ${getLanguageName(displayLanguage)}`
    : t("platform.common.sourceText", "Source");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 h-8 px-2.5 text-xs font-medium"
        >
          <IconProvider iconstring="fa-languageslucide" size={16} />
          <span>{currentLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        <DropdownMenuItem
          onClick={() => setDisplayLanguage(null)}
          className="gap-2 text-xs"
        >
          <span className="flex-1">
            {t("platform.common.sourceText", "Source")}
          </span>
          {displayLanguage === null && (
            <Check className="h-3.5 w-3.5 text-primary" />
          )}
        </DropdownMenuItem>
        {availableLanguages.map((langCode) => (
          <DropdownMenuItem
            key={langCode}
            onClick={() => setDisplayLanguage(langCode)}
            className="gap-2 text-xs"
          >
            <span>{getLanguageFlag(langCode)}</span>
            <span className="flex-1">{getLanguageName(langCode)}</span>
            {displayLanguage === langCode && (
              <Check className="h-3.5 w-3.5 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DisplayLanguageSelector;
